import { useState, useEffect, useRef } from "react"
import API from "../../utils/API"
import Sweetalert from "../../utils/Sweetalert"
import { SPINNER_DELAY_MS } from "./constants"
import "./AiTargetingStats.css"

function formatPresenceTime(ms) {
  if (!ms || ms <= 0) return "0s"

  const seconds = ms / 1000
  if (seconds < 60) return `${Math.round(seconds * 10) / 10}s`

  const minutes = Math.floor(seconds / 60)
  const remainingSeconds = Math.round(seconds % 60)

  return `${minutes}m ${remainingSeconds}s`
}

function formatRate(rate) {
  if (rate == null || isNaN(rate)) return "0%"

  return `${Math.round(rate * 100)}%`
}

const Spinner = () => <i className="fas fa-sync-alt fa-spin"></i>

function buildCards(trafficData, adData) {
  return [
    {
      label: "Nombre de passages total",
      value: (trafficData?.totalDetected ?? 0).toLocaleString("fr-FR"),
      icon: "fas fa-users",
      bg: "bg-info",
      tooltip: `Nombre total de passages sur l'ensemble des écrans sélectionnés pendant la période.\n\nValeur : ${(trafficData?.totalDetected ?? 0).toLocaleString("fr-FR")} passage${(trafficData?.totalDetected ?? 0) >= 2 ? "s" : ""}`,
    },
    {
      label: "Nombre de diffusions totales",
      value: (adData?.totalImpressions ?? 0).toLocaleString("fr-FR"),
      icon: "fas fa-ad",
      bg: "bg-purple",
      tooltip: `Nombre total d'affichages publicitaires diffusés sur les écrans sélectionnés pendant la période (ciblés + par défaut).\n\nValeur : ${(adData?.totalImpressions ?? 0).toLocaleString("fr-FR")} diffusion${(adData?.totalImpressions ?? 0) >= 2 ? "s" : ""}`,
    },
    {
      label: "Temps d'exposition moyen",
      value: formatPresenceTime(trafficData?.avgPresenceMs),
      icon: "fas fa-clock",
      bg: "bg-warning",
      tooltip: `Durée moyenne pendant laquelle une personne reste devant l'écran, calculée de l'entrée à la sortie du champ de la caméra.\n\nValeur : ${formatPresenceTime(trafficData?.avgPresenceMs)}`,
    },
    {
      label: "Taux de ciblage",
      value: formatRate(adData?.targetingRate),
      icon: "fas fa-bullseye",
      bg: "bg-danger",
      tooltip: `Pourcentage d'impressions ciblées par rapport au total. Un taux élevé signifie que l'IA adapte efficacement les publicités à l'audience détectée.\n\nValeur : ${formatRate(adData?.targetingRate)}`,
    },
  ]
}

export default function UnifiedKpiBar({ queryParams }) {
  const [trafficData, setTrafficData] = useState(null)
  const [adData, setAdData] = useState(null)
  const [showSpinner, setShowSpinner] = useState(true)
  const spinnerTimerRef = useRef(null)
  const hasPreviousData = useRef(false)

  useEffect(() => {
    const controller = new AbortController()

    clearTimeout(spinnerTimerRef.current)

    if (!hasPreviousData.current) {
      setShowSpinner(true)
    } else {
      spinnerTimerRef.current = setTimeout(() => setShowSpinner(true), SPINNER_DELAY_MS)
    }

    API.GET(
      `/ai-targeting-stats/kpis?${queryParams}`,
      (response) => {
        setTrafficData(response.data.traffic)
        setAdData(response.data.ads)
        clearTimeout(spinnerTimerRef.current)
        setShowSpinner(false)
        hasPreviousData.current = true
      },
      () => {
        Sweetalert.fireErrorToast("Impossible de charger les KPIs")
        setTrafficData(null)
        setAdData(null)
        clearTimeout(spinnerTimerRef.current)
        setShowSpinner(false)
        hasPreviousData.current = true
      },
      { signal: controller.signal }
    )

    return () => {
      controller.abort()
      clearTimeout(spinnerTimerRef.current)
    }
  }, [queryParams])

  const cards = buildCards(trafficData, adData)

  return (
    <div className="ai-stats-kpi-bar row justify-content-between">
      {cards.map((card) => (
        <div key={card.label} className="col-lg-4 col-md-4 col-6 ai-stats-kpi-bar__col">
          <div className="ai-stats-kpi-bar__wrapper">
            <div className="info-box ai-stats-kpi-bar__box">
              <span className={`info-box-icon ${card.bg}`}>
                <i className={card.icon}></i>
              </span>
              <div className="info-box-content">
                <span className="info-box-text">{card.label}</span>
                <span className="info-box-number">
                  {showSpinner ? <Spinner /> : card.value}
                </span>
              </div>
            </div>
            {card.tooltip && (
              <div className="ai-stats-kpi-bar__tooltip">
                {card.tooltip.split("\n").map((line, i) => (
                  <span key={i}>
                    {line}
                    {i < card.tooltip.split("\n").length - 1 && <br />}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  )
}
